/*! Copyright (c) Safe As Milk. All rights reserved. */
import Cart from "cart-store";

const DEPOSIT_GROUP_PROPERTY = "_deposit_group_id";
const DEPOSIT_ROLE_PROPERTY = "_deposit_role";
const DEPOSIT_CENTS_PROPERTY = "_deposit_cents";

// Bovengrens om oneindige loops te voorkomen wanneer er onverwacht continu
// nieuwe inconsistenties verschijnen (bv. door een externe app die regels
// toevoegt). 25 iteraties is ruim genoeg voor realistische winkelmandjes.
const MAX_RECONCILE_ITERATIONS = 25;

// Serieel uitvoeren: elke call hangt achter de vorige aan en geeft een eigen
// promise terug. Een caller die awaitet weet dus dat zijn ronde écht is
// uitgevoerd voordat de promise resolved — geen "ik kreeg de promise van een
// eerdere ronde terug die net klaar was"-race.
let reconcileChain = Promise.resolve();

function getCartItemProperty(item, property) {
    return item?.properties?.[property] || null;
}

function getDepositConfig() {
    const depositData = document.getElementById("cart-deposit-data");
    if (!depositData) {
        return {
            productId: null,
            variantsByAmount: {}
        };
    }
    try {
        return JSON.parse(depositData.textContent);
    } catch (_e) {
        return {
            productId: null,
            variantsByAmount: {}
        };
    }
}

function createDepositGroupId() {
    return `deposit-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function getDepositGroupState(cart) {
    return (cart?.items || []).reduce(((groups, item) => {
        const depositGroupId = getCartItemProperty(item, DEPOSIT_GROUP_PROPERTY);
        if (!depositGroupId) return groups;
        if (!groups.has(depositGroupId)) {
            groups.set(depositGroupId, {
                mainItems: [],
                depositItems: []
            });
        }
        const group = groups.get(depositGroupId);
        const role = getCartItemProperty(item, DEPOSIT_ROLE_PROPERTY);
        if (role === "main") {
            group.mainItems.push(item);
        } else if (role === "deposit") {
            group.depositItems.push(item);
        }
        return groups;
    }), new Map);
}

// Geeft de eerste fix terug die nog moet gebeuren, in volgorde van prioriteit:
//   1. Dubbele deposit-lines binnen één groep verwijderen.
//   2. Deposit zonder main verwijderen.
//   3. Deposit met afwijkende quantity corrigeren naar mainQty.
//   4. Main mét deposit_cents maar zonder bijbehorende deposit-line: deposit
//      opnieuw toevoegen.
async function applyOneFix(cart) {
    const groups = getDepositGroupState(cart);
    // Stap 1 & 2 — opruimen
    for (const {mainItems: mainItems, depositItems: depositItems} of groups.values()) {
        if (depositItems.length > 1) {
            const duplicate = depositItems[1];
            console.warn("[deposit] Dubbele deposit-line gevonden — verwijderen", duplicate.key);
            return await Cart.change({
                id: duplicate.key,
                quantity: 0
            });
        }
        if (depositItems.length === 1 && mainItems.length === 0) {
            const orphan = depositItems[0];
            console.warn("[deposit] Deposit zonder main — verwijderen", orphan.key);
            return await Cart.change({
                id: orphan.key,
                quantity: 0
            });
        }
    }
    // Stap 3 — quantities synchroniseren
    for (const {mainItems: mainItems, depositItems: depositItems} of groups.values()) {
        if (mainItems.length === 0 || depositItems.length === 0) continue;
        const mainQty = mainItems.reduce(((sum, i) => sum + Number(i.quantity || 0)), 0);
        const depositItem = depositItems[0];
        const depQty = Number(depositItem.quantity || 0);
        if (mainQty !== depQty) {
            console.warn(`[deposit] Quantity mismatch (main=${mainQty}, deposit=${depQty}) — corrigeren naar ${mainQty}`);
            return await Cart.change({
                id: depositItem.key,
                quantity: mainQty
            });
        }
    }
    // Stap 4 — ontbrekende deposit opnieuw plaatsen
    for (const [groupId, {mainItems: mainItems, depositItems: depositItems}] of groups.entries()) {
        if (mainItems.length === 0 || depositItems.length > 0) continue;
        const mainItem = mainItems[0];
        const cents = Number(getCartItemProperty(mainItem, DEPOSIT_CENTS_PROPERTY));
        if (!cents || cents <= 0) continue;
        const config = getDepositConfig();
        const variantId = config.variantsByAmount?.[cents.toString()];
        if (!variantId) {
            console.warn(`[deposit] Geen statiegeld-variant gevonden voor ${cents} cent — kan deposit niet herstellen.`);
            continue;
        }
        const totalMainQty = mainItems.reduce(((sum, i) => sum + Number(i.quantity || 0)), 0);
        console.warn(`[deposit] Deposit ontbreekt voor groep ${groupId} — opnieuw plaatsen (${totalMainQty}x)`);
        return await Cart.add([ {
            id: variantId,
            quantity: totalMainQty,
            "properties[_deposit_group_id]": groupId,
            "properties[_deposit_role]": "deposit",
            "properties[_deposit_cents]": String(cents),
            "properties[Type]": "Statiegeld",
            "properties[Voor]": mainItem.product_title || "Product"
        } ]);
    }
    return null;
}

function reconcileDepositItems(initialCart) {
    // Hang aan de chain. Elke nieuwe call krijgt zijn eigen "ronde" die start
    // ná de vorige. Zo weet een caller die awaitet dat zíjn check daadwerkelijk
    // is uitgevoerd op de op-dat-moment-actuele cart-state.
    const myWork = reconcileChain.then((async () => {
        // Bewust pas hier de cart ophalen: tegen de tijd dat onze ronde aan de
        // beurt is kan de wereld al weer verder zijn.
        let cart = initialCart || Cart.getState().cart;
        let iteration = 0;
        try {
            while (iteration < MAX_RECONCILE_ITERATIONS) {
                iteration++;
                const result = await applyOneFix(cart);
                if (!result) break;
                cart = result;
            }
            if (iteration >= MAX_RECONCILE_ITERATIONS) {
                console.error("[deposit] Maximum reconcile-iteraties bereikt — er is mogelijk een hardnekkige inconsistentie.");
            }
        } catch (error) {
            console.error("[deposit] Reconciliation faalde:", error);
        }
    }));
    // Vang ook eventuele errors hier op zodat een gefaalde ronde niet de hele
    // chain breekt voor latere callers.
    reconcileChain = myWork.catch((() => {}));
    return myWork;
}

// Controleert of de cart consistent is qua statiegeld. Geeft een lijst met
// gevonden problemen terug; lege lijst betekent: alles in orde.
function findDepositInconsistencies(cart = Cart.getState().cart) {
    const issues = [];
    const groups = getDepositGroupState(cart);
    for (const [groupId, {mainItems: mainItems, depositItems: depositItems}] of groups.entries()) {
        if (depositItems.length > 1) {
            issues.push({
                type: "DUPLICATE_DEPOSIT",
                groupId: groupId
            });
        }
        if (mainItems.length === 0 && depositItems.length > 0) {
            issues.push({
                type: "ORPHAN_DEPOSIT",
                groupId: groupId
            });
        }
        if (mainItems.length > 0 && depositItems.length === 0) {
            const cents = Number(getCartItemProperty(mainItems[0], DEPOSIT_CENTS_PROPERTY));
            if (cents > 0) {
                issues.push({
                    type: "MISSING_DEPOSIT",
                    groupId: groupId
                });
            }
        }
        if (mainItems.length > 0 && depositItems.length > 0) {
            const mainQty = mainItems.reduce(((sum, i) => sum + Number(i.quantity || 0)), 0);
            const depQty = Number(depositItems[0].quantity || 0);
            if (mainQty !== depQty) {
                issues.push({
                    type: "QUANTITY_MISMATCH",
                    groupId: groupId,
                    mainQty: mainQty,
                    depQty: depQty
                });
            }
        }
    }
    return issues;
}

function isCartDepositConsistent(cart = Cart.getState().cart) {
    return findDepositInconsistencies(cart).length === 0;
}

// ============================================================================
// Checkout-guard
//
// Voorkomt dat een klant via de cart-form (of de cart-drawer) afrekent met een
// inconsistente statiegeld-staat. We onderscheppen de submit, draaien een
// reconcile, en pas wanneer alles klopt laten we de submit door.
// ============================================================================
const ERROR_BANNER_CLASS = "deposit-guard-error";

function isCartCheckoutForm(form) {
    if (!(form instanceof HTMLFormElement)) return false;
    // form.action geeft de absolute URL terug; we matchen op pad.
    let path;
    try {
        path = new URL(form.action, window.location.origin).pathname;
    } catch (_e) {
        path = form.getAttribute("action") || "";
    }
    if (!/^\/(.*\/)?cart\/?$/.test(path) && !/^\/(.*\/)?cart$/.test(path)) {
        // Sommige cart-formulieren hebben action="/cart" zonder trailing slash —
        // val terug op een eenvoudige bevat-check.
        if (!/\/cart(\?|$|\/)/.test(path)) return false;
    }
    return Boolean(form.querySelector('button[name="checkout"]'));
}

function findCheckoutSubmitter(form, event) {
    if (event && event.submitter && event.submitter.name === "checkout") return event.submitter;
    return form.querySelector('button[name="checkout"]');
}

function showGuardError(form, message) {
    let banner = form.querySelector(`.${ERROR_BANNER_CLASS}`);
    if (!banner) {
        banner = document.createElement("div");
        banner.classList.add(ERROR_BANNER_CLASS, "errors", "qty-error", "u-small");
        const checkoutButton = form.querySelector('button[name="checkout"]');
        if (checkoutButton) {
            checkoutButton.parentNode.insertBefore(banner, checkoutButton);
        } else {
            form.appendChild(banner);
        }
    }
    banner.textContent = message;
}

function clearGuardError(form) {
    const banner = form.querySelector(`.${ERROR_BANNER_CLASS}`);
    if (banner) banner.remove();
}

// Event-delegated submit-guard: vangt élke submit op een cart-form af, ook
// wanneer het formulier later in de DOM verschijnt (cart-drawer template,
// AJAX-rerenders, modals etc). Eén centrale handler is duurzamer dan
// per-form listeners.
async function handleDelegatedSubmit(event) {
    const form = event.target;
    if (!isCartCheckoutForm(form)) return;
    // Tweede pass — door requestSubmit() na een geslaagde reconcile.
    if (form.dataset.depositGuardCleared === "1") {
        form.dataset.depositGuardCleared = "";
        return;
    }
    const submitter = findCheckoutSubmitter(form, event);
    // Geen checkout-submit (bv. de 'update'-knop in het noscript-pad)
    // → niet onderscheppen.
    if (!submitter || submitter.name !== "checkout") return;
    event.preventDefault();
    clearGuardError(form);
    submitter.disabled = true;
    submitter.classList.add("is-loading");
    try {
        await Cart.refresh();
        await reconcileDepositItems();
        await Cart.refresh();
        const issues = findDepositInconsistencies();
        if (issues.length > 0) {
            console.error("[deposit] Cart blijft inconsistent na reconcile:", issues);
            showGuardError(form, "Er gaat iets mis met het statiegeld in je winkelmand. Vernieuw de pagina en probeer het opnieuw.");
            submitter.disabled = false;
            submitter.classList.remove("is-loading");
            return;
        }
        form.dataset.depositGuardCleared = "1";
        // De submitter moet weer enabled zijn voordat we requestSubmit
        // aanroepen — anders weigert de browser het submit-event.
        submitter.disabled = false;
        submitter.classList.remove("is-loading");
        if (typeof form.requestSubmit === "function") {
            form.requestSubmit(submitter);
        } else {
            form.submit();
        }
    } catch (error) {
        console.error("[deposit] Checkout-guard faalde:", error);
        showGuardError(form, "Er is iets fout gegaan bij het afrekenen. Probeer het opnieuw.");
        submitter.disabled = false;
        submitter.classList.remove("is-loading");
    }
}

// ============================================================================
// Boot
// ============================================================================
document.addEventListener("on:cart:loaded", (event => {
    void reconcileDepositItems(event.detail?.cart || Cart.getState().cart);
}));

// Eén centrale, gedelegeerde submit-listener — werkt ook voor formulieren die
// later in de DOM verschijnen (cart-drawer, modals, AJAX-rerenders).
document.addEventListener("submit", handleDelegatedSubmit, true);

void reconcileDepositItems(Cart.getState().cart);

export { reconcileDepositItems, isCartDepositConsistent, findDepositInconsistencies };
//# sourceMappingURL=cart-deposit-sync.js.map
